'use client';
import React, { useEffect, useRef, useState } from 'react';
import { isMemberDayTag } from '@/components/constants/memberdayTagName';
import { getOfferCampaignTagNames } from '@/helpers/get-offer-campaign-tag-names';
import CloseIcon from '@/assets/icons/close.svg';

import { BlockWrapper } from '../../../../../components/block-wrapper/block-wrapper';
import { Breadcrumbs } from '../../../../../components/breadcrumbs';
import { CardSubTenant } from '../../../../../components/cards/card-sub-tenant';
import ImageWrapper from '../../../../../components/image-wrapper/Image-wrapper';
import { PageComponentImage } from '../../../../../components/page-components/image/page-component-image';
import ShowMore from '../../../../../components/page-components/list-page/show-more';
import IkeaProductFeed from '../../../../../components/page-components/tenant-detail-page/ikea-product-feed/ikea-product-feed';
import TenantDescription from '../../../../../components/page-components/tenant-detail-page/tenant-description';
import { TenantInfo } from '@/components/page-components/tenant-info/tenant-info';
import { Share } from '../../../../../components/share/share';
import { FavouriteButton } from '@/components/ui/favourite-button/favourite-button';
import { ListCard } from '../../../../../helpers/list-page/interfaces/list-card.interface';
import { createSubTenantCard } from '../../../../../helpers/list-page/list-page-helpers';
import { cardIsSubTenant } from '../../../../../helpers/list-page/type-guards';
import useGetMobileAppWebViewHeader from '../../../../../hooks/use-get-mobile-app-web-view';
import { useIsMobile } from '../../../../../hooks/use-is-mobile';
import { IMeetingplacePageHomeMinifiedExtendedOpeningHours } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageTenantShopItem } from '@/lib/fragments/meetingPlacePageTenantShopItem';
import AttributeFilterIcons from '../attribute-filter-icons';
import {
  useGetActiveEvents,
  useGetActiveOffers,
  useGetLanguageArray,
  useGetLibraryConstantsDictionaryData,
  useGetTimezone,
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import OfferCard from '@/components/offercard/offer-card';
import { getBrowserClient } from '@/lib/apolloClient';
import { GET_ACTIVE_OFFERS } from '@/lib/queries/meetingPlacePageOfferQuery';
import ViewOnMap from '@/components/page-components/tenant-detail-page/view-on-map';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import Button from '@/components/ui/button/button';
import { useUser } from '@/providers/UserProvider';
import { checkIfValidUser, resolveSitePathPrefix } from '@/helpers/utils';
import { redirect } from 'next/navigation';
import { SideModal } from '@/components/ui/side-modal/side-modal';
import { SideModalBody } from '@/components/ui/side-modal/side-modal-body';
import LoginRegister from '@/components/user-profile/login-register';
import Backdrop from '@/components/ui/backdrop/backdrop';
import { GET_MATCHING_EVENTS } from '@/lib/queries/meetingPlacePageEventQuery';
import { EventCard } from '@/components/event-card/event-card';
import { WithCountdown } from '@/components/cards/wrapper/WithCountdown';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import { useIsSmallScreen } from '@/hooks/use-is-small-screen';
import { getTagBorderColor } from '@/helpers/tailwindColors';
import { getListingPageSlugs } from '@/lib/queries/getEventAndOfferListPageSlug';
import { isBrowser } from '@/helpers/is-browser';
import { isDateTimeInPast } from '@/helpers/dates';

interface MeetingPlacePageTenantShopProps {
  metaData: any;
  node_locale: string;
  shopData: IMeetingPlacePageTenantShopItem | null;
  homeData: IMeetingplacePageHomeMinifiedExtendedOpeningHours | null;
  preview: boolean;
  parent: any;
  // matchingOffers: IMeetingPlacePageOfferItem[];
  // languageArray: string[];
  // dictionary: any;
}

const MeetingPlacePageTenantShop = ({
  metaData,
  node_locale,
  shopData,
  homeData,
  preview,
  parent,
  // matchingOffers,
  // languageArray,
  // dictionary,
}: MeetingPlacePageTenantShopProps) => {
  const config = useTenantConfig();
  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();
  const languageArray = useGetLanguageArray();
  const activeOfferIds = useGetActiveOffers();
  const activeEvents = useGetActiveEvents();
  const timezone = useGetTimezone();
  const dictionary = useGetLibraryConstantsDictionaryData();
  const [matchingOffers, setMatchingOffers] = useState([]);
  const [matchingEvents, setMatchingEvents] = useState([]);
  const client = getBrowserClient(preview);
  const isSmallScreen = useIsSmallScreen();
  const currentPathAfterLogin = useRef('');
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY == 'true';

  const prefix = resolveSitePathPrefix(config?.GATSBY_SITE_PATH_PREFIX);
  const page = shopData;
  const homepage = homeData;

  const tenantDescription = page?.description
    ? page.description
    : page?.brand && page.brand?.description
      ? page.brand.description
      : undefined;

  const image = page?.coverImage
    ? page.coverImage
    : page?.brand && page.brand?.coverImage
      ? page.brand.coverImage
      : undefined;

  const logo = page?.logo
    ? page.logo
    : page?.brand && page.brand?.logo
      ? page.brand.logo
      : undefined;

  const pageName = page?.name ? page.name : page?.brand?.name;

  const eventIdsOfCurrentTenant = activeEvents
    ?.filter((event: any) => event?.tenant?.sys?.id === page?.sys?.id)
    ?.map((event) => event?.sys?.id);

  const isLegoBrandPage =
    page?.brand && page?.brand?.brandId?.includes('ACC0031511');

  const isMobile = useIsMobile();
  const isWebView = useGetMobileAppWebViewHeader();

  const [sliderOpen, setSliderOpen] = useState(false);
  const [mobileSliderOpen, setMobileSliderOpen] = useState(false);
  const { user } = useUser();
  // const user_token = Cookies.get('user_token');

  const [eventListPageSlug, setEventListPageSlug] = useState<string | null>(
    null,
  );
  const [offerListPageSlug, setOfferListPageSlug] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const fetchSlugs = async () => {
      const tag =
        config?.NEXT_PUBLIC_MEETINGPLACE_TAG_CONTENTFUL_ID ||
        config?.MEETINGPLACE_TAG_CONTENTFUL_ID ||
        '';
      const { eventListPageSlug, offerListPageSlug } =
        await getListingPageSlugs(client, tag, node_locale, preview);
      if (eventListPageSlug) setEventListPageSlug(eventListPageSlug);
      if (offerListPageSlug) setOfferListPageSlug(offerListPageSlug);
    };

    fetchSlugs();
  }, [node_locale]);

  useEffect(() => {
    mobileSliderOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [mobileSliderOpen]);

  useEffect(() => {
    //  Filter offers for current tenant
    const offerIdsOfCurrentTenant = activeOfferIds
      ?.filter((offer: any) => offer?.tenant?.sys?.id === page?.sys?.id)
      ?.map((offer) => offer?.sys?.id);
    const fetchData = async () => {
      try {
        const result = await client.query({
          query: GET_ACTIVE_OFFERS,
          variables: {
            ids: offerIdsOfCurrentTenant,
            language: node_locale,
          },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        const offers =
          (result?.data as any)?.meetingPlacePageOfferCollection?.items || [];
        setMatchingOffers(offers);
      } catch (error) {
        console.error('Error fetching offers:', error);
      }
    };

    fetchData();
  }, [activeOfferIds, node_locale]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await client.query({
          query: GET_MATCHING_EVENTS,
          variables: {
            ids: eventIdsOfCurrentTenant,
            language: node_locale,
          },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        const events =
          (result?.data as any)?.meetingPlacePageEventCollection?.items || [];
        setMatchingEvents(events);
      } catch (error) {
        console.error('Error fetching Events:', error);
      }
    };
    fetchData();
  }, [activeEvents, node_locale]);

  const offerList = matchingOffers || [];
  const eventsList = (matchingEvents || []).filter(
    (event: any) =>
      !event?.endDate || !isDateTimeInPast(event.endDate, timezone),
  );

  // Has offer for promotion icon in detail page
  if (page) {
    page.hasOffer = offerList.length > 0;
    if (offerList.length > 0) {
      const allTags = offerList.flatMap((o: any) =>
        getOfferCampaignTagNames(o),
      );
      page.offerCampaignNames = [...new Set(allTags)] as string[];
    }
  }
  if (page) page.hasEvent = eventsList.length > 0;

  const attributeFilters = homepage?.meetingPlace?.attributeFilters;

  const [currOfferPage, setCurrOfferPage] = useState(1);
  const _offersListSize = 4;
  const slicedOfferList = offerList?.slice(0, _offersListSize * currOfferPage);
  const slicedEventList = eventsList?.slice(0, 4);

  // Multi Brand
  const subShops = page?.subShopItemCollection?.items;
  const subShopCardList =
    subShops &&
    subShops?.filter(Boolean)?.map((page) => {
      const urlSubdirectory = getUrlSubdirectory(
        urlSubDirectorydictionary,
        node_locale,
        page?.__typename,
      );
      return createSubTenantCard(
        page,
        urlSubdirectory,
        node_locale || page?.sys?.locale,
        prefix,
      );
    });

  const [currSubTenantPage, setCurrSubTenantPage] = useState(1);
  const _subShopsSize = 6;
  const slicedList = subShopCardList?.slice(
    0,
    _subShopsSize * currSubTenantPage,
  );

  // Ikea Page
  const isIkeaPage =
    page?.brand && page?.brand?.slug ? page?.brand?.slug === 'ikea' : false;

  const primaryLanguage = languageArray[0] || node_locale;

  const gtmParams: IGtmParameters = {
    event: 'view_shop_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };

  // const updatedPageName = pageName + pageContext?.htmlTitleSuffix;
  const updatedPageName = pageName;

  const aspectRatio =
    logo?.width && logo?.height
      ? parseInt(logo.width) / parseInt(logo.height)
      : undefined;

  const isNearlySquare =
    typeof aspectRatio === 'number' && Math.abs(aspectRatio - 1) < 0.1;

  useSendGtmEvent(gtmParams);

  const renderOffers = () => {
    return (
      <div className="grid grid-cols-1 gap-5 pt-6 sm:grid-cols-2">
        {matchingOffers?.map((offer: any) => {
          const parentSlug = getUrlSubdirectory(
            urlSubDirectorydictionary,
            node_locale,
            offer?.__typename ?? '',
          );
          return (
            <OfferCard
              key={offer.sys.id}
              offer={offer}
              logo={offer?.tenant?.logo}
              node_locale={node_locale}
              header={offer.name}
              parentSlug={parentSlug || ''}
            />
          );
        })}
      </div>
    );
  };

  const renderEvents = () => {
    return (
      <>
        <div className="pt-6 font-title text-headingm text-neutral-grey-700">
          Events
        </div>
        <div className="grid grid-cols-1 gap-5 pt-3 sm:grid-cols-2">
          {slicedEventList?.map((event: any, index: number) => {
            const parentSlug = getUrlSubdirectory(
              urlSubDirectorydictionary,
              node_locale,
              event?.__typename ?? '',
            );
            return (
              <EventCard
                key={index}
                card={event}
                cardIndex={index}
                node_locale={node_locale}
                parentSlug={parentSlug || ''}
                dictionary={dictionary}
                fixedWidth={isSmallScreen}
              />
            );
          })}
        </div>
        {/* <div className='text-right'>
        <LinkButton variant="secondary" to={""}>See All Events</LinkButton>
        </div> */}
      </>
    );
  };

  const renderSubTenants = () => {
    return (
      <>
        <h2 className="pt-6 font-title text-headingm text-neutral-grey-700">
          {page?.subShopsListTitle}
        </h2>
        <div className="mb-6 grid grid-cols-2 gap-x-7 gap-y-6 pt-6 sm:grid-cols-3">
          {slicedList?.map((card: ListCard, cardIndex: number) => {
            if (cardIsSubTenant(card)) {
              return (
                <CardSubTenant
                  card={card}
                  cardIndex={cardIndex}
                  key={card.id}
                />
              );
            }
          })}
        </div>
      </>
    );
  };

  const sendEventToDataLayer = () => {
    if (isBrowser && dataLayer) {
      dataLayer.push({
        event: 'kiddyland-opened',
        page_title: pageName,
        user_status: isLivliFamily ? 'logged_in' : undefined,
      });
    }
  };

  return (
    <>
      {page && (
        <>
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ? page?.name : page?.brand?.name}
          />

          {isMobile ? (
            <>
              <div className="w-full rounded-b-xxl bg-background-primary-light px-6 pb-8">
                <Breadcrumbs
                  breadcrumbs={[pageName]}
                  homeText={homepage?.breadcrumbHomeText ?? ''}
                  cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
                  node_locale={node_locale}
                  parent={parent}
                />
                <div className="pt-5">
                  <PageComponentImage image={image} node_locale={node_locale} />
                </div>
                <div className="mt-6">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      {logo?.url && (
                        <ImageWrapper
                          url={logo?.url}
                          alt={logo?.description ? logo.description : ''}
                          className="h-12 w-12 rounded-md bg-brand-white flex items-center justify-center border border-brand-grey-2"
                          imgClassName={
                            isNearlySquare
                              ? '!object-contain rounded-md'
                              : '!object-contain rounded-md max-w-full max-h-full p-1'
                          }
                          width={
                            image?.width ? parseInt(image.width) : undefined
                          }
                          height={
                            image?.height ? parseInt(image.height) : undefined
                          }
                        />
                      )}
                      <h1 className="ml-2 font-title text-headings text-text-default">
                        {pageName}
                      </h1>
                    </div>

                    <FavouriteButton
                      id={page?.sys?.id ?? ''}
                      type="shop"
                      className="h-12 w-12"
                      iconclass="w-6 h-6"
                      name={pageName}
                    />
                  </div>

                  {/* // <div className="flex items-center justify-between">
                  //   <PageComponentMainHeader header={pageName} />
                  // </div> */}
                  {homepage && (
                    <TenantInfo
                      page={page}
                      homepage={homepage}
                      node_locale={node_locale}
                      isMobile={isMobile}
                      variant="categories"
                      parentPage={parent}
                    />
                  )}
                  {isLivliFamily && isLegoBrandPage && (
                    <Button
                      size="regular"
                      variant="primary"
                      className="w-full mt-4"
                      onClick={() => {
                        const queueUrl =
                          '/' +
                          [prefix, node_locale, 'kl'].filter(Boolean).join('/') +
                          '?mpid=' +
                          homepage?.meetingPlace?.businessUnitCode;
                        if (user && checkIfValidUser(user)) {
                          sendEventToDataLayer();
                          redirect(queueUrl);
                        } else {
                          currentPathAfterLogin.current = queueUrl;
                          setMobileSliderOpen(!mobileSliderOpen);
                        }
                      }}
                    >
                      {dictionary?.joinQueue}
                    </Button>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    {attributeFilters && (
                      <AttributeFilterIcons
                        page={page}
                        attributeFilters={attributeFilters}
                      />
                    )}
                    {homepage?.meetingPlace.showSocialShare && (
                      <Share className="relative ml-auto" />
                    )}
                  </div>
                </div>
              </div>
              {isLivliFamily && (
                <div className="flex flex-wrap gap-x-3 gap-y-2 mt-6 mx-6">
                  {page?.offerCampaignNames?.some((t: string) =>
                    isMemberDayTag(t),
                  ) && (
                    <div
                      className={`px-3 py-2 text-bodys -tracking-[0.12px] font-bold text-neutral-grey-700 break-words inline-flex justify-center items-center bg-background-primary-light rounded-md border-2 border-brand-yellow-1`}
                    >
                      {dictionary?.memberDayText}
                    </div>
                  )}
                  {isLivliFamily &&
                    page?.circularServicesCollection?.items.map((tag) => {
                      const borderColor = getTagBorderColor('green');
                      return (
                        <>
                          <div
                            className={`px-3 py-2 text-bodys -tracking-[0.12px] font-bold text-neutral-grey-700 break-words inline-flex justify-center items-center bg-background-primary-light rounded-md border-2 ${borderColor}`}
                          >
                            {tag?.tagName}
                          </div>
                        </>
                      );
                    })}
                </div>
              )}

              <div className="mt-8 px-6">
                {homepage && (
                  <TenantInfo
                    page={page}
                    node_locale={node_locale}
                    homepage={homepage}
                    variant="topInfo"
                    isMobile={isMobile}
                  />
                )}
                {(page?.floorPlanIDs?.length > 0 ||
                  (page?.unitId && page?.unitId?.length > 0)) &&
                  homepage?.meetingPlace.visioGlobeMapKey &&
                  homepage.meetingPlace.visioGlobeFloorPlanPage && (
                    <ViewOnMap page={page} homepage={homepage} />
                  )}
                {tenantDescription && (
                  <TenantDescription
                    description={tenantDescription}
                    node_locale={node_locale}
                  />
                )}
                <div className="-mx-6 mt-10 bg-brand-white sm:px-6 pt-8 lg:m-0 lg:p-0 lg:pt-0">
                  {slicedOfferList && slicedOfferList?.length > 0 && (
                    <>
                      <h2 className="px-5 sm:px-0 mt-6 mb-3 font-title text-headingm text-neutral-grey-900">
                        {dictionary?.offers}
                      </h2>
                      <div className="mb:0 py-2 px-5 sm:p-2 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                        {slicedOfferList?.map((offer: any) => {
                          const logo =
                            offer?.tenant && offer?.tenant.logo
                              ? offer.tenant.logo
                              : offer?.tenant?.brand && offer.tenant.brand?.logo
                                ? offer.tenant.brand.logo
                                : undefined;
                          const tenantName =
                            offer?.tenant && offer.tenant.name
                              ? offer.tenant.name
                              : offer?.tenant && offer.tenant?.brand
                                ? offer.tenant.brand.name
                                : undefined;
                          const parentSlug = getUrlSubdirectory(
                            urlSubDirectorydictionary,
                            node_locale,
                            offer?.__typename ?? '',
                          );
                          return (
                            <WithCountdown key={offer?.sys?.id} card={offer}>
                              {({ countdownState }) => (
                                <OfferCard
                                  offer={offer}
                                  header={tenantName}
                                  node_locale={node_locale}
                                  logo={logo}
                                  key={offer?.sys?.id}
                                  countdownState={countdownState}
                                  parentSlug={parentSlug || ''}
                                />
                              )}
                            </WithCountdown>
                          );
                        })}
                      </div>

                      <div>
                        {offerListPageSlug && (
                          <LinkWrapper
                            data={`/${node_locale}/${offerListPageSlug}`}
                            className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                          >
                            {dictionary?.seeAllOffers}
                          </LinkWrapper>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {eventsList && eventsList?.length > 0 && (
                  <div className="-mx-6 bg-brand-white px-6 pt-6 lg:m-0 lg:p-0 lg:pt-0">
                    <>
                      <h2 className="mt-6 mb-3 font-title text-headingm text-text-default">
                        {dictionary?.events}
                      </h2>
                      <div className="mb:0 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                        {slicedEventList?.map((event: any, index: number) => {
                          const parentSlug = getUrlSubdirectory(
                            urlSubDirectorydictionary,
                            node_locale,
                            event?.__typename ?? '',
                          );
                          return (
                            <EventCard
                              key={index}
                              card={event}
                              cardIndex={index}
                              node_locale={node_locale}
                              parentSlug={parentSlug || ''}
                              dictionary={dictionary}
                              fixedWidth={isSmallScreen}
                            />
                          );
                        })}
                      </div>
                      <div>
                        {eventListPageSlug && (
                          <LinkWrapper
                            data={`/${node_locale}/${eventListPageSlug}`}
                            className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                          >
                            {dictionary?.seeAllEvents}
                          </LinkWrapper>
                        )}
                      </div>
                    </>
                  </div>
                )}
                <div className="-mx-6 mt-10 bg-brand-white px-6 pt-6 lg:m-0 lg:p-0">
                  {page?.isItAMultiBrandStore === 'Yes' &&
                    subShopCardList &&
                    subShopCardList.length > 0 && (
                      <>
                        {renderSubTenants()}
                        {subShopCardList.length > _subShopsSize - 1 && (
                          <ShowMore
                            currentPage={currSubTenantPage}
                            maxNum={subShopCardList.length}
                            setPage={setCurrSubTenantPage}
                            pageSize={_subShopsSize}
                            ofString={dictionary?.of}
                            showMoreString={dictionary?.showMore}
                            showingString={dictionary?.showing}
                          />
                        )}
                      </>
                    )}
                  {isIkeaPage && (
                    <IkeaProductFeed
                      dictionary={dictionary}
                      node_locale={node_locale}
                      businessUnitCode={
                        homepage?.meetingPlace?.businessUnitCode || ''
                      }
                      primaryLanguage={primaryLanguage}
                      pageName={updatedPageName}
                    />
                  )}
                  {homepage && (
                    <TenantInfo
                      page={page}
                      node_locale={node_locale}
                      homepage={homepage}
                      variant="bottomInfo"
                      isMobile={isMobile}
                    />
                  )}
                </div>
              </div>
              {mobileSliderOpen && (
                <>
                  <Backdrop
                    handleOpen={() => {
                      setMobileSliderOpen(!mobileSliderOpen);
                    }}
                    showButtons={false}
                    className="!min-h-[250px]"
                  >
                    <div className="h-[40vh]">
                      <LoginRegister
                        node_locale={node_locale}
                        brandId={homepage?.meetingPlace?.businessUnitCode || ''}
                        config={config}
                        current_path={currentPathAfterLogin.current}
                        handleClose={() => {
                          setMobileSliderOpen(false);
                        }}
                      />
                    </div>
                    <button
                      className="absolute -top-20 right-4 z-[200] grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
                      onClick={() => {
                        setMobileSliderOpen(false);
                      }}
                    >
                      <CloseIcon />
                    </button>
                  </Backdrop>
                </>
              )}
            </>
          ) : (
            <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
              <Breadcrumbs
                breadcrumbs={[pageName]}
                homeText={homepage?.breadcrumbHomeText ?? ''}
                cssClass="lg:ml-22"
                node_locale={node_locale}
                parent={parent}
              />
              <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
                <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
                  <div className="lg:sticky lg:top-0">
                    <PageComponentImage
                      image={image}
                      node_locale={node_locale}
                    />
                    {homepage && (
                      <TenantInfo
                        page={page}
                        homepage={homepage}
                        node_locale={node_locale}
                        isMobile={isMobile}
                        parentPage={parent}
                      />
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <BlockWrapper>
                    <div className="items-start justify-between flex w-full mb-6">
                      <div className="flex items-center">
                        {logo?.url && (
                          <ImageWrapper
                            url={logo?.url}
                            alt={logo?.description ? logo.description : ''}
                            className="h-12 w-12 lg:h-16 lg:w-16 rounded-md  bg-brand-white flex items-center justify-center border border-brand-grey-2"
                            imgClassName={
                              isNearlySquare
                                ? '!object-contain rounded-md'
                                : '!object-contain rounded-md max-w-full max-h-full p-1'
                            }
                            width={
                              image?.width ? parseInt(image.width) : undefined
                            }
                            height={
                              image?.height ? parseInt(image.height) : undefined
                            }
                          />
                        )}
                        <h1 className="ml-2 font-title text-headingm text-text-default">
                          {pageName}
                        </h1>
                      </div>
                      <FavouriteButton
                        id={page?.sys?.id ?? ''}
                        type="shop"
                        className="w-16 h-16"
                        iconclass="w-[30px] h-[28px]"
                        name={pageName}
                      />
                    </div>
                    <div className="flex items-center gap-2 ml-auto mb-3">
                      {attributeFilters && (
                        <AttributeFilterIcons
                          page={page}
                          attributeFilters={attributeFilters}
                        />
                      )}
                    </div>
                    {isLivliFamily && (
                      <div className="flex flex-wrap gap-x-3 gap-y-2 mt-6 mb-4">
                        {page?.offerCampaignNames?.some((t: string) =>
                          isMemberDayTag(t),
                        ) && (
                          <div
                            className={`px-3 py-2 text-bodys -tracking-[0.12px] font-bold text-neutral-grey-700 break-words inline-flex justify-center items-center bg-background-primary-light rounded-md border-2 border-brand-yellow-1`}
                          >
                            {dictionary?.memberDayText}
                          </div>
                        )}
                        {isLivliFamily &&
                          page?.circularServicesCollection?.items.map((tag) => {
                            const borderColor = getTagBorderColor('green');
                            return (
                              <>
                                <div
                                  className={`px-3 py-2 text-bodys -tracking-[0.12px] font-bold text-neutral-grey-700 break-words inline-flex justify-center items-center bg-background-primary-light rounded-md border-2 ${borderColor}`}
                                >
                                  {tag?.tagName}
                                </div>
                              </>
                            );
                          })}
                      </div>
                    )}
                    {/* <PageComponentMainHeader header={pageName} /> */}
                    {/* <PageComponentMainBottomBorder /> */}
                    {isLivliFamily && isLegoBrandPage && (
                      <Button
                        size="regular"
                        variant="primary"
                        className="mt-4 mb-4"
                        onClick={() => {
                          const queueUrl =
                            '/' +
                            [prefix, node_locale, 'kl']
                              .filter(Boolean)
                              .join('/') +
                            '?mpid=' +
                            homepage?.meetingPlace?.businessUnitCode;
                          if (user && checkIfValidUser(user)) {
                            sendEventToDataLayer();
                            redirect(queueUrl);
                          } else {
                            currentPathAfterLogin.current = queueUrl;
                            // setLocalStorageItem('current_path', queueUrl);
                            setSliderOpen(true);
                          }
                        }}
                      >
                        {dictionary?.joinQueue}
                      </Button>
                    )}
                    {tenantDescription && (
                      <TenantDescription
                        description={tenantDescription}
                        node_locale={node_locale}
                      />
                    )}
                    {(page?.floorPlanIDs?.length > 0 ||
                      (page?.unitId && page?.unitId?.length > 0)) &&
                      homepage?.meetingPlace.visioGlobeMapKey &&
                      homepage.meetingPlace.visioGlobeFloorPlanPage && (
                        <ViewOnMap page={page} homepage={homepage} />
                      )}
                    {homepage?.meetingPlace.showSocialShare && (
                      <Share className="absolute -bottom-2 right-0 lg:bottom-8 lg:right-20 xl:right-24" />
                    )}
                  </BlockWrapper>
                  {slicedOfferList && slicedOfferList?.length > 0 && (
                    <div className="-mx-6 mt-10 bg-brand-white px-6 pt-8 lg:m-0 lg:p-0 lg:pt-0">
                      <>
                        <h2 className="mt-6 mb-3 font-title text-headingm text-neutral-grey-900">
                          {dictionary?.offers}
                        </h2>
                        <div className="p-2 mb:0 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                          {slicedOfferList?.map((offer: any) => {
                            const logo =
                              offer?.tenant && offer?.tenant.logo
                                ? offer.tenant.logo
                                : offer?.tenant?.brand &&
                                    offer.tenant.brand?.logo
                                  ? offer.tenant.brand.logo
                                  : undefined;
                            const tenantName =
                              offer?.tenant && offer.tenant.name
                                ? offer.tenant.name
                                : offer?.tenant && offer.tenant?.brand
                                  ? offer.tenant.brand.name
                                  : undefined;

                            const parentSlug = getUrlSubdirectory(
                              urlSubDirectorydictionary,
                              node_locale,
                              offer?.__typename ?? '',
                            );
                            return (
                              <WithCountdown key={offer?.sys?.id} card={offer}>
                                {({ countdownState }) => (
                                  <OfferCard
                                    offer={offer}
                                    header={tenantName}
                                    node_locale={node_locale}
                                    logo={logo}
                                    key={offer?.sys?.id}
                                    countdownState={countdownState}
                                    parentSlug={parentSlug || ''}
                                  />
                                )}
                              </WithCountdown>
                            );
                          })}
                        </div>

                        <div>
                          {offerListPageSlug && (
                            <LinkWrapper
                              data={`/${node_locale}/${offerListPageSlug}`}
                              className="flex ml-auto w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                            >
                              {dictionary?.seeAllOffers}
                            </LinkWrapper>
                          )}
                        </div>
                      </>
                    </div>
                  )}
                  <div className="-mx-6 mt-10 bg-brand-white px-6 pt-6 lg:m-0 lg:p-0 lg:pt-0">
                    {eventsList && eventsList?.length > 0 && (
                      <>
                        <h2 className="mt-6 mb-3 font-title text-headingm text-text-default">
                          {dictionary?.events}
                        </h2>
                        <div className="mb:0 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pb-6 sm:grid sm:grid-cols-2 sm:gap-5">
                          {slicedEventList?.map((event: any, index: number) => {
                            const parentSlug = getUrlSubdirectory(
                              urlSubDirectorydictionary,
                              node_locale,
                              event?.__typename ?? '',
                            );
                            return (
                              <EventCard
                                key={index}
                                card={event}
                                cardIndex={index}
                                node_locale={node_locale}
                                parentSlug={parentSlug || ''}
                                dictionary={dictionary}
                                fixedWidth={isSmallScreen}
                              />
                            );
                          })}
                        </div>
                        <div>
                          {eventListPageSlug && (
                            <LinkWrapper
                              data={`/${node_locale}/${eventListPageSlug}`}
                              className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                            >
                              {dictionary?.seeAllEvents}
                            </LinkWrapper>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="-mx-6 mt-10 bg-brand-white px-6 pt-6 lg:m-0 lg:p-0">
                    {page?.isItAMultiBrandStore === 'Yes' &&
                      subShopCardList &&
                      subShopCardList.length > 0 && (
                        <>
                          {renderSubTenants()}
                          {subShopCardList.length > _subShopsSize - 1 && (
                            <ShowMore
                              currentPage={currSubTenantPage}
                              maxNum={subShopCardList.length}
                              setPage={setCurrSubTenantPage}
                              pageSize={_subShopsSize}
                              ofString={dictionary?.of}
                              showMoreString={dictionary?.showMore}
                              showingString={dictionary?.showing}
                            />
                          )}
                        </>
                      )}
                    {isIkeaPage && (
                      <IkeaProductFeed
                        dictionary={dictionary}
                        node_locale={node_locale}
                        primaryLanguage={primaryLanguage}
                        businessUnitCode={
                          homepage?.meetingPlace?.businessUnitCode || ''
                        }
                        pageName={updatedPageName}
                      />
                    )}
                  </div>
                </div>
              </div>
              <>
                <SideModal
                  isOpen={sliderOpen}
                  handleClose={() => setSliderOpen(false)}
                >
                  <SideModalBody bodyClass="h-full !my-0 !py-16">
                    <LoginRegister
                      node_locale={node_locale}
                      brandId={homepage?.meetingPlace?.businessUnitCode || ''}
                      config={config}
                      current_path={currentPathAfterLogin.current}
                      handleClose={() => {
                        setSliderOpen(false);
                      }}
                    />
                  </SideModalBody>
                </SideModal>
              </>
            </main>
          )}
        </>
      )}
    </>
  );
};
export default MeetingPlacePageTenantShop;

